
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { NAVIGATION_SERVICE, NavigationService, PageDefinition } from "@cervval.js/client-html5-spa/navigation";
import { CjsIconFa } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { ID } from "@cervval.js/common-action/src/types";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { ObjectTools } from "@cervval.js/common/tools";
import { addressToPlainAddress } from "@ehealth_tools/client-map/src/components/tools/map.tools";
import { BookingCompany, BookingProfessional } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { MapProfessional } from "@ehealth_tools/common-map/src/map.definitions";
import { BookingPages } from "../../../page.i18n";
import { AbstractClientBookingService } from "../../../services/abstractClientBooking.service";
import { BookingPageName } from "../../booking.pages";

export const mapPages = {
    "map": ObjectTools.is<PageDefinition>({
        importPromise: () => {
            return import("@ehealth_tools/client-map/src/components/scheduleMap/scheduleMap").then((module) => new module.ScheduleMap<MapProfessional & { id: ID, pro: BookingProfessional, address: BookingCompany }>({
                googleApiToken: injector.get<AbstractClientBookingService>(BOOKING_SERVICE).googleMapToken,
                googleMapUid: injector.get<AbstractClientBookingService>(BOOKING_SERVICE).googleMapUid,
                webInterface: true,
                getProfessionals: () => {
                    const mapStructures: (MapProfessional & { id: ID, pro: BookingProfessional, address: BookingCompany })[] = [];
                    const bookingService = injector.get<AbstractClientBookingService>(BOOKING_SERVICE);
                    const pros = bookingService.getProfessional({ onlyAdvertising: true });
                    for (const pro of pros) {
                        const company = bookingService.getCompany(pro);
                        if (company != null) {
                            mapStructures.push({
                                id: pro.id,
                                address: company,
                                displayName: `${pro.givenName} ${pro.familyName}`,
                                specialties: [],
                                pro
                            });
                        }
                    }
                    return mapStructures;
                },
                onRDVClick: (professional, ts, productId) => {
                    const navigationService = injector.get<NavigationService<BookingPageName>>(NAVIGATION_SERVICE);
                    const bkService = injector.get<AbstractClientBookingService>(BOOKING_SERVICE);
                    if (!navigationService.pageNames.includes("booking")) {
                        bkService.showLogin();
                        return;
                    }

                    bkService.setUserToInteractWithById(professional.id);
                    bkService.bookingDate = ts;
                    bkService.setBookingProductById(productId);
                    navigationService.navigate("booking");
                },
                getProfessionalProducts: (professional) => {
                    const bookingService = injector.get<AbstractClientBookingService>(BOOKING_SERVICE);
                    const proProducts = bookingService.getFilteredProducts(true).filter(v => v.professionalId === professional.id);
                    proProducts.sort((a, b) => a.duration - b.duration);
                    return proProducts.map(v => ({ value: v.id, label: v.name }));
                },
                getNextAvailableDate: (professional, productId) => {
                    const bookingService = injector.get<AbstractClientBookingService>(BOOKING_SERVICE);
                    const proProducts = bookingService.getProducts().filter(v => v.professionalId === professional.id);
                    proProducts.sort((a, b) => a.duration - b.duration);
                    const quickestProduct = proProducts.find(v => v.id === productId) ?? proProducts[0];
                    if (quickestProduct == null) { return undefined; }
                    return bookingService.getNextSlot(quickestProduct, professional.id);
                },
                displayExtraData: (professional) => {
                    if (professional == null) { return undefined; }
                    const div = createHtmlElement("div", { cssClass: ["d-flex", "flex-column"] });

                    const address = createHtmlElement("div", { cssClass: ["d-flex", "gap-1"] });
                    address.append(new CjsIconFa({ name: "fa-location-dot", fixedWidth: true }));
                    address.append(createHtmlElement("div", { innerHTML: addressToPlainAddress(professional.address) }));
                    div.append(address);

                    const phone = createHtmlElement("div", { cssClass: ["d-flex", "gap-1"] });
                    phone.append(new CjsIconFa({ name: "fa-phone", fixedWidth: true }));
                    phone.append(createHtmlElement("div", { innerHTML: professional.pro.phoneNumber ?? "" }));
                    div.append(phone);


                    return div;
                },
                isMember: (professional) => {
                    return injector.get<AbstractClientBookingService>(BOOKING_SERVICE).isMember(professional.pro);
                },
                saveLocation: (dto, location) => {
                    const bookingService = injector.get<AbstractClientBookingService>(BOOKING_SERVICE);
                    return bookingService.saveLocation(dto, location);
                }
            }));
        },
        menuItem: {
            label: BookingPages().map,
            iconClasses: "fa-regular fa-map"
        },
        menus: [{
            menuUid: "bookingMenu",
            order: 1,
            sectionUid: "mainSection"
        }]
    })
};